<template>
    <div class="ai-capabilities" :class="{ 'ai-capabilities--compact': compact }">
        <!-- Themenleiste: alle Bereiche auf einen Blick -->
        <div class="cap-topics" role="tablist" aria-label="What Quaro AI can do">
            <button
                v-for="topic in topics"
                :key="topic.id"
                type="button"
                role="tab"
                class="cap-topic"
                :class="{ 'cap-topic--active': topic.id === activeId, 'cap-topic--locked': !topic.available }"
                :aria-selected="topic.id === activeId ? 'true' : 'false'"
                @click="activeId = topic.id">
                <v-icon small class="cap-topic-icon">{{ topic.available ? topic.icon : "mdi-lock-outline" }}</v-icon>
                <span>{{ topic.title }}</span>
            </button>
        </div>

        <!-- Beispiele des gewählten Themas. Auf der Chat-Seite immer hell (die Seite bleibt in beiden App-Themes hell, siehe
             SEOKI.vue); im Dialog dem App-Theme folgen. -->
        <v-card v-if="active" flat :light="!compact" class="cap-panel" role="tabpanel">
            <div class="cap-panel-head">
                <div class="cap-icon">
                    <v-icon small>{{ active.icon }}</v-icon>
                </div>
                <div class="cap-panel-title">
                    <div class="cap-title">{{ active.title }}</div>
                    <div class="cap-text">{{ active.text }}</div>
                </div>
            </div>
            <div v-if="!active.available" class="cap-locked">
                <v-icon x-small>mdi-lock-outline</v-icon>
                Not included in your plan - these are examples of what you could ask.
            </div>
            <div v-else-if="active.uses" class="cap-uses">
                <v-icon x-small>mdi-star-outline</v-icon>
                {{ active.usesLabel || "Based on" }}: <strong>{{ active.uses }}</strong>
            </div>
            <div class="cap-prompts">
                <button
                    v-for="(prompt, i) in activePrompts"
                    :key="active.id + i"
                    type="button"
                    class="cap-prompt"
                    :disabled="!active.available"
                    :tabindex="active.available ? 0 : -1"
                    @click="$emit('use-prompt', prompt.text)">
                    <span>
                        <template v-for="(part, j) in prompt.parts">
                            <strong v-if="part.own" :key="j" class="cap-own">{{ part.text }}</strong>
                            <template v-else>{{ part.text }}</template>
                        </template>
                    </span>
                    <v-icon small class="cap-prompt-arrow">mdi-arrow-up-left</v-icon>
                </button>
            </div>
            <div v-if="active.available" class="cap-hint">Click an example to copy it into the input field - adjust it if needed and press Enter.</div>
        </v-card>
    </div>
</template>

<script>
    import { buildCapabilities, visiblePrompts, isSuiteAvailable } from "./helper/aiChatCapabilities"

    export default {
        name: "AiChatCapabilities",
        props: {
            // Antwort von POST /v2/sources/chat-examples (neuestes Objekt je Bereich) - null solange sie nicht geladen ist
            examples: { type: Object, default: null },
            // Variante im Dialog (folgt dem App-Theme)
            compact: { type: Boolean, default: false }
        },
        data() {
            return {
                // gewähltes Thema; null = das erste der Leiste
                selectedId: null
            }
        },
        computed: {
            topics() {
                // Eine nicht gebuchte Suite bleibt in der Leiste (sichtbarer Mehrwert), ihre Beispiele sind aber gesperrt.
                // Reihenfolge fest wie im Helper (eigene Projekte zuerst), gesperrte zuletzt - bewusst NICHT nach geladenen
                // Projektnamen umsortiert, sonst springt die Leiste, sobald die Beispiele vom Server kommen.
                const rank = (topic) => (topic.available ? 0 : 1)
                return buildCapabilities(this.examples)
                    .map((topic, index) => ({ ...topic, index, available: isSuiteAvailable(this.$store.state, topic.suite) }))
                    .sort((a, b) => rank(a) - rank(b) || a.index - b.index)
            },
            activeId: {
                get() {
                    const known = this.topics.some((topic) => topic.id === this.selectedId)
                    return known ? this.selectedId : this.topics.length ? this.topics[0].id : null
                },
                set(id) {
                    this.selectedId = id
                }
            },
            active() {
                return this.topics.find((topic) => topic.id === this.activeId) || null
            },
            activePrompts() {
                return visiblePrompts(this.active, (suite) => isSuiteAvailable(this.$store.state, suite))
            }
        }
    }
</script>

<style scoped>
    /* Tönungen aus der Theme-Farbe (White-Label: OBI orange, quickseo blau); ohne color-mix-Unterstützung grau */
    .cap-topics {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
        margin-bottom: 14px;
    }
    .cap-topic {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        border-radius: 999px;
        font-size: 0.875rem;
        font-weight: 600;
        color: inherit;
        background: rgba(255, 255, 255, 0.75);
        border: 1px solid rgba(0, 0, 0, 0.1);
        cursor: pointer;
        transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
    }
    .cap-topic:hover,
    .cap-topic:focus-visible {
        border-color: var(--v-primary-base);
        outline: none;
    }
    .cap-topic-icon {
        color: var(--v-primary-base) !important;
    }
    .cap-topic--active {
        background: var(--v-primary-base);
        border-color: var(--v-primary-base);
        color: #fff;
    }
    .cap-topic--active .cap-topic-icon {
        color: #fff !important;
    }
    .cap-topic--locked:not(.cap-topic--active) {
        opacity: 0.6;
    }
    .cap-topic--locked:not(.cap-topic--active) .cap-topic-icon {
        color: inherit !important;
    }
    .ai-capabilities--compact .cap-topic {
        background: transparent;
        border-color: rgba(128, 128, 128, 0.4);
    }
    .ai-capabilities--compact .cap-topic--active {
        background: var(--v-primary-base);
    }

    .cap-panel {
        padding: 18px 20px 14px;
        border-radius: 16px !important;
        background: rgba(255, 255, 255, 0.82) !important;
        border: 1px solid rgba(0, 0, 0, 0.07) !important;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
    }
    .cap-panel.theme--dark {
        background: rgba(255, 255, 255, 0.05) !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
    }
    .cap-panel-head {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 10px;
    }
    .cap-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        flex: 0 0 34px;
        border-radius: 10px;
        background: rgba(128, 128, 128, 0.12);
        background: color-mix(in srgb, var(--v-primary-base) 12%, transparent);
    }
    .cap-icon .v-icon {
        color: var(--v-primary-base) !important;
    }
    .cap-title {
        font-weight: 700;
        font-size: 1rem;
        line-height: 1.3;
    }
    .cap-text {
        font-size: 0.8125rem;
        line-height: 1.4;
        opacity: 0.72;
    }
    .cap-uses,
    .cap-locked {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 0.78rem;
        margin-bottom: 4px;
        opacity: 0.85;
    }
    .cap-uses strong {
        color: var(--v-primary-base);
    }
    .cap-uses .v-icon,
    .cap-locked .v-icon {
        color: inherit !important;
    }
    .cap-locked {
        font-weight: 700;
    }
    .cap-prompts {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        margin-top: 8px;
    }
    .cap-prompt {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        width: 100%;
        text-align: left;
        font-size: 0.875rem;
        line-height: 1.4;
        color: inherit;
        background: rgba(128, 128, 128, 0.08);
        background: color-mix(in srgb, var(--v-primary-base) 7%, transparent);
        border: 1px solid transparent;
        border-radius: 12px;
        padding: 10px 12px;
        cursor: pointer;
        transition: background-color 0.12s ease, border-color 0.12s ease;
    }
    .cap-prompt:disabled {
        cursor: default;
        opacity: 0.6;
    }
    .cap-prompt:not(:disabled):hover,
    .cap-prompt:not(:disabled):focus-visible {
        background: rgba(128, 128, 128, 0.16);
        background: color-mix(in srgb, var(--v-primary-base) 14%, transparent);
        border-color: var(--v-primary-base);
        outline: none;
    }
    .cap-prompt-arrow {
        margin-top: 1px;
        opacity: 0;
        transition: opacity 0.12s ease;
        color: var(--v-primary-base) !important;
    }
    .cap-prompt:not(:disabled):hover .cap-prompt-arrow,
    .cap-prompt:not(:disabled):focus-visible .cap-prompt-arrow {
        opacity: 1;
    }
    .cap-own {
        font-weight: 700;
        color: var(--v-primary-base);
    }
    .cap-hint {
        margin-top: 10px;
        font-size: 0.75rem;
        opacity: 0.6;
    }
    /* nur im Dialog (dort folgt das Feld dem App-Theme) */
    .cap-panel.theme--dark .cap-prompt {
        background: rgba(255, 255, 255, 0.06);
        background: color-mix(in srgb, var(--v-primary-base) 16%, transparent);
    }
    .cap-panel.theme--dark .cap-own,
    .cap-panel.theme--dark .cap-uses strong {
        color: inherit;
    }
    @media (max-width: 700px) {
        .cap-prompts {
            grid-template-columns: minmax(0, 1fr);
        }
        /* schmal: eine wischbare Zeile statt vier umbrochener */
        .cap-topics {
            justify-content: flex-start;
            flex-wrap: nowrap;
            overflow-x: auto;
            scrollbar-width: none;
            padding-bottom: 2px;
        }
        .cap-topics::-webkit-scrollbar {
            display: none;
        }
        .cap-topic {
            flex: 0 0 auto;
        }
    }
</style>
